import { useClerk } from '@clerk/react'
import { LogOutIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { Pagina } from '@/components/Pagina'
import { AvatarIniciales } from '@/components/marca/Avatar'
import { AvatarRastro, Logo } from '@/components/marca/Logo'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
  MenuSeparador,
} from '@/components/ui/MenuDesplegable'
import type { UsuarioSesion } from '@/features/auth/useSesion'
import { olvidarUltimoProyecto } from '@/features/auth/useSesion'

interface EncabezadoProps {
  usuario: UsuarioSesion
  /** Título de la vista: "Grilla · DOCBA · Trimestre oct–dic 2026" (§5.1). */
  titulo?: ReactNode
  /** Contenido entre el título y las acciones: enlaces, buscador (P03). */
  centro?: ReactNode
  /** Acciones a la derecha, antes del avatar (Administración, etc.). */
  acciones?: ReactNode
  /** Ítems del menú del avatar antes de Salir (Equipo RASTRO, §5.2). */
  menuExtra?: ReactNode
  /** En celular el logo cede el lugar a este control (p. ej. "‹ atrás"). */
  antesDelLogo?: ReactNode
}

/**
 * C1: logo, título y avatar con menú. El logo lleva al inicio, como en
 * cualquier sitio: para el cliente, su proyecto; para RASTRO, Administración,
 * que es de donde salen todos los clientes y proyectos.
 * El menú muestra el nombre y, debajo,
 * **la dirección de Google con la que está la sesión**: resuelve la confusión
 * más frecuente del portal (§5.1, §12.4). Salir siempre.
 * Cliente: iniciales. RASTRO: short logo sobre círculo negro.
 */
export function Encabezado({
  usuario,
  titulo,
  centro,
  acciones,
  menuExtra,
  antesDelLogo,
}: EncabezadoProps) {
  const clerk = useClerk()
  const navigate = useNavigate()

  // Se cierra la sesión y se navega con el router del portal. La redirección
  // que hace Clerk por su cuenta dejaba la página en blanco en Chrome de Android.
  const salir = () => {
    olvidarUltimoProyecto()
    void clerk.signOut().then(() => navigate('/ingreso', { replace: true }))
  }

  return (
    <header className="sticky top-0 z-40 border-b border-borde bg-lienzo/90 backdrop-blur">
      <Pagina className="flex h-14 items-center gap-3">
        {antesDelLogo}
        <Link
          to={usuario.tipo === 'rastro' ? '/admin' : '/'}
          aria-label="Ir al inicio"
          className="shrink-0 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tinta"
        >
          <Logo variante="primario" className="hidden h-7 sm:block" />
          <Logo variante="corto" className="h-7 sm:hidden" />
        </Link>

        {/*
          El logotipo trae aire transparente desigual: sobre un lienzo de 213
          píxeles de alto, el trazo va del 18 al 178, con 34 de aire abajo y 18
          arriba. Al centrar la imagen, su línea base queda unos 5 píxeles por
          debajo de la del título. Se baja el título 3 para que las dos líneas
          base se lean a la misma altura.
        */}
        <div className="min-w-0 flex-1 translate-y-[3px] truncate text-sm font-medium">
          {titulo}
        </div>

        {centro}
        {acciones}

        <MenuDesplegable>
          <MenuDisparador
            className="rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tinta"
            aria-label="Menú de la cuenta"
          >
            {usuario.tipo === 'rastro' ? (
              <AvatarRastro />
            ) : (
              <AvatarIniciales nombre={usuario.nombre} />
            )}
          </MenuDisparador>
          <MenuContenido className="min-w-56">
            <div className="px-3 py-2">
              <p className="truncate text-sm font-medium">{usuario.nombre}</p>
              <p className="truncate text-xs text-tinta-suave" data-sesion-email>
                {usuario.email}
              </p>
            </div>
            <MenuSeparador className="my-1 h-px bg-borde" />
            {menuExtra}
            <MenuItem onSelect={salir}>
              <LogOutIcon className="size-4" aria-hidden />
              Salir
            </MenuItem>
          </MenuContenido>
        </MenuDesplegable>
      </Pagina>
    </header>
  )
}
